import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import Layout from "@components/Layout";
import { useStores } from "@stores";
import localizations from "@src/localizations";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import useIsMobile from "@src/hooks/useIsMobile";
import { track, EVENTS } from "@src/utils/analytics";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  streamChatMessage,
  sendFeedback,
  listConversations as apiListConversations,
  getConversation as apiGetConversation,
  deleteConversation as apiDeleteConversation,
  resolveAgentAction,
  type AgentAction,
} from "@services/agentService";

/**
 * No images in assistant replies. The reply can quote tool data that someone
 * else controls (a John Deere machine name, a saved memory), and an image tag
 * makes the browser request its URL the moment it renders, without a click:
 * a query string on that URL is enough to carry data out. Links stay; they need
 * a click, and react-markdown already strips javascript: hrefs.
 */
const AGENT_DISALLOWED_ELEMENTS = ["img"];

// ── Types ─────────────────────────────────────────────────

interface DisplayMessage {
  id: number;
  role: "user" | "assistant";
  text: string;
  feedback?: "up" | "down" | null;
  /** Changes this reply proposed; each waits for Confirm/Cancel. */
  actions?: AgentAction[];
}

interface Conversation {
  id: string;
  title: string;
  messages: DisplayMessage[];
  createdAt: number;
  updatedAt: number;
}

// ── Styled components ─────────────────────────────────────

const ScreenRoot = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  height: calc(100vh - 160px);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: calc(var(--vh-full, 100vh) - 140px);
  }
`;

/* ── History sidebar ── */

const Sidebar = styled.div`
  width: 240px;
  min-width: 240px;
  border-right: 1px solid rgba(137, 144, 147, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 8px;
  border-bottom: 1px solid rgba(137, 144, 147, 0.15);
`;

const SidebarTitle = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 13px;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NewChatBtn = styled.button`
  border: none;
  background: #68ad18;
  color: #fff;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  transition: background 0.2s;

  &:hover {
    background: #5a9a14;
  }
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
`;

const ConversationItem = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: none;
  background: ${({ active }) => (active ? "rgba(104, 173, 24, 0.1)" : "transparent")};
  cursor: pointer;
  padding: 8px 12px;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ active }) =>
      active ? "rgba(104, 173, 24, 0.15)" : "rgba(137, 144, 147, 0.08)"};
  }
`;

const ConversationTitle = styled.span<{ active: boolean }>`
  font-family: Roboto, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${({ active }) => (active ? "#68ad18" : "#494c5b")};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const DeleteBtn = styled.span`
  color: #c0c0c0;
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;
  padding: 0 2px;
  line-height: 1;

  &:hover {
    color: #d32f2f;
  }
`;

const EmptySidebar = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  color: #c0c0c0;
  text-align: center;
`;

/* ── Chat area ── */

const ChatArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const MessagesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const MessagesArea = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 100%;
    padding: 0 8px 16px;
  }
`;

const MessageRow = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ isUser }) => (isUser ? "flex-end" : "flex-start")};
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  word-break: break-word;

  ${({ isUser }) =>
    isUser
      ? `
    background: #68ad18;
    color: #fff;
    border-bottom-right-radius: 4px;
  `
      : `
    background: #f0f0f0;
    color: #494c5b;
    border-bottom-left-radius: 4px;
  `}

  /* Markdown styles for agent messages */
  ${({ isUser }) =>
    !isUser &&
    `
    h1, h2, h3 {
      font-family: 'Nunito Sans', sans-serif;
      font-weight: bold;
      margin: 8px 0 4px;
    }
    h1 { font-size: 16px; }
    h2 { font-size: 15px; }
    h3 { font-size: 14px; }
    p { margin: 4px 0; }
    p:first-of-type { margin-top: 0; }
    p:last-of-type { margin-bottom: 0; }

    table {
      border-collapse: collapse;
      width: 100%;
      margin: 8px 0;
      font-size: 13px;
    }
    th, td {
      border: 1px solid #d0d0d0;
      padding: 6px 8px;
      text-align: left;
    }
    th {
      background: #e4e4e4;
      font-weight: bold;
    }
    tr:nth-of-type(even) {
      background: #f7f7f7;
    }

    ul, ol { padding-left: 20px; margin: 4px 0; }
    li { margin: 2px 0; }

    code {
      background: #e4e4e4;
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 13px;
    }
    pre {
      background: #e4e4e4;
      padding: 8px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 4px 0;
    }
    pre code {
      background: none;
      padding: 0;
    }

    strong { font-weight: 600; }
    a { color: #68ad18; text-decoration: underline; }
  `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 90%;
  }
`;

const WelcomeContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 24px;
  min-height: 0;
`;

const WelcomeText = styled.p`
  font-family: Roboto, sans-serif;
  font-size: 15px;
  line-height: 22px;
  color: #899093;
  text-align: center;
  max-width: 400px;
  margin: 0;
`;

const SuggestionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  max-width: 480px;
`;

const SuggestionChip = styled.button`
  border: 1px solid rgba(104, 173, 24, 0.5);
  background: rgba(104, 173, 24, 0.06);
  color: #4a7c2e;
  border-radius: 16px;
  padding: 7px 14px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(104, 173, 24, 0.15);
  }
`;

const ToolChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const ToolChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(104, 173, 24, 0.08);
  border: 1px solid rgba(104, 173, 24, 0.25);
  color: #4a7c2e;
  border-radius: 12px;
  padding: 4px 10px;
  font-family: Roboto, sans-serif;
  font-size: 12px;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border: 2px solid rgba(104, 173, 24, 0.3);
    border-top-color: #68ad18;
    border-radius: 50%;
    animation: agent-spin 0.8s linear infinite;
  }

  @keyframes agent-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const FeedbackRow = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 4px;
`;

const FeedbackBtn = styled.button<{ active: boolean }>`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 4px;
  opacity: ${({ active }) => (active ? 1 : 0.35)};
  filter: ${({ active }) => (active ? "none" : "grayscale(60%)")};
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
    background: rgba(137, 144, 147, 0.1);
  }
`;

const RetryBtn = styled.button`
  border: 1px solid #d32f2f;
  background: transparent;
  color: #d32f2f;
  border-radius: 6px;
  padding: 4px 12px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  cursor: pointer;
  margin-top: 6px;

  &:hover {
    background: rgba(211, 47, 47, 0.08);
  }
`;

const InputBarWrap = styled.div`
  flex-shrink: 0;
  border-top: 1px solid rgba(137, 144, 147, 0.3);
  padding: 12px 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 10px 8px;
  }
`;

const InputBar = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 720px;
  margin: 0 auto;
`;

const ChatInput = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 8px;
  padding: 10px 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #494c5b;
  outline: none;
  min-height: 20px;
  max-height: 120px;
  box-sizing: border-box;

  &:focus {
    border-color: #68ad18;
  }

  &::placeholder {
    color: #899093;
  }

  &:disabled {
    background: #f8f8f8;
  }
`;

const SendButton = styled.button<{ disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${({ disabled }) => (disabled ? "#c8c8c8" : "#68ad18")};
  color: #fff;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #5a9a14;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const StopButton = styled(SendButton)`
  background: #d32f2f;

  &:hover:not(:disabled) {
    background: #b71c1c;
  }
`;

const ThinkingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;

  span {
    font-family: Roboto, sans-serif;
    font-size: 13px;
    color: #899093;
    font-style: italic;
  }
`;

const ActionCard = styled.div`
  width: 80%;
  max-width: 420px;
  margin-top: 8px;
  border: 1px solid rgba(104, 173, 24, 0.35);
  border-radius: 12px;
  background: #fff;
  padding: 12px 14px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  color: #494c5b;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 90%;
  }
`;

const ActionTitle = styled.div`
  font-family: "Nunito Sans", sans-serif;
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 8px;
`;

const ActionChanges = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;

  dt {
    color: #899093;
  }
  dd {
    margin: 0;
    word-break: break-word;
  }
  s {
    color: #899093;
    margin-right: 6px;
  }
`;

const ColorSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
  vertical-align: middle;
  border: 1px solid rgba(0, 0, 0, 0.15);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ActionConfirmBtn = styled.button`
  border: none;
  background: #68ad18;
  color: #fff;
  border-radius: 6px;
  padding: 6px 14px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ActionCancelBtn = styled.button`
  border: 1px solid rgba(137, 144, 147, 0.5);
  background: transparent;
  color: #494c5b;
  border-radius: 6px;
  padding: 6px 14px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ActionStatus = styled.div<{ failed: boolean }>`
  margin-top: 10px;
  font-size: 13px;
  color: ${({ failed }) => (failed ? "#d32f2f" : "#4a7c2e")};
`;

// ── Icons ─────────────────────────────────────────────────

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
  </svg>
);

const StopIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
  </svg>
);

// ── Helpers ───────────────────────────────────────────────

function generateLocalId(): string {
  return "local-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function newConversation(): Conversation {
  return {
    id: generateLocalId(),
    title: "",
    messages: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

const isLocalId = (id: string) => id.startsWith("local-");

const DATE_KEYS = new Set(["start", "end", "stop"]);

/** A changed value as the card shows it: dates local, booleans yes/no, colours with a swatch. */
function formatActionValue(
  key: string,
  value: unknown,
  t: { actionYes: string; actionNo: string }
): React.ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? t.actionYes : t.actionNo;
  if (DATE_KEYS.has(key) && typeof value === "string") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    }
  }
  if (key === "color" && typeof value === "string") {
    return (
      <>
        <ColorSwatch style={{ background: value }} />
        {value}
      </>
    );
  }
  return String(value);
}

// ── Component ─────────────────────────────────────────────

const AgentScreen: React.FC = () => {
  const { settingsStore } = useStores();
  const lang = localizations[settingsStore.language];
  const isMobile = useIsMobile();

  // Conversations state — loaded from server (history lives server-side)
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const active = conversations.find((c) => c.id === activeId) ?? null;
  const messages = useMemo(() => active?.messages ?? [], [active]);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFailed, setLastFailed] = useState<string | null>(null);
  const [activeTools, setActiveTools] = useState<string[]>([]);
  const [resolvingActionId, setResolvingActionId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load conversations from server on mount
  useEffect(() => {
    apiListConversations().then((convos) => {
      const mapped: Conversation[] = convos.map((c) => ({
        id: c.id,
        title: c.title,
        messages: c.messages as DisplayMessage[],
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
      }));
      setConversations(mapped);
      if (mapped.length > 0) setActiveId(mapped[0].id);
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, activeTools, scrollToBottom]);

  // Abort an in-flight stream when leaving the screen
  useEffect(() => () => abortRef.current?.abort(), []);

  // ── Conversation management ──

  const patchConversation = useCallback(
    (id: string, updater: (c: Conversation) => Conversation) => {
      setConversations((prev) => prev.map((c) => (c.id === id ? updater(c) : c)));
    },
    []
  );

  const handleNewChat = () => {
    const convo = newConversation();
    setConversations((prev) => [convo, ...prev]);
    setActiveId(convo.id);
    setInput("");
    setError(null);
    setLastFailed(null);
  };

  const handleSelectChat = (id: string) => {
    setActiveId(id);
    setInput("");
    setError(null);
    setLastFailed(null);

    // Refresh server-canonical messages (feedback, cross-device edits)
    if (!isLocalId(id)) {
      apiGetConversation(id).then((full) => {
        if (full) {
          patchConversation(id, (c) => ({
            ...c,
            title: full.title,
            messages: full.messages as DisplayMessage[],
          }));
        }
      });
    }
  };

  const handleDeleteChat = (id: string) => {
    if (!isLocalId(id)) apiDeleteConversation(id);
    setConversations((prev) => {
      const remaining = prev.filter((c) => c.id !== id);
      if (activeId === id) {
        setActiveId(remaining.length > 0 ? remaining[0].id : null);
      }
      return remaining;
    });
  };

  // ── Message handling (streaming) ──

  const runTurn = async (text: string, appendUser: boolean) => {
    setError(null);
    setLastFailed(null);

    let convoId = activeId;
    let convo = conversations.find((c) => c.id === convoId);
    if (!convoId || !convo) {
      convo = newConversation();
      convoId = convo.id;
      setConversations((prev) => [convo!, ...prev]);
      setActiveId(convoId);
    }

    const baseMax = convo.messages.reduce((m, x) => Math.max(m, x.id), 0);
    const userMsgId = appendUser ? baseMax + 1 : baseMax;
    const assistantId = userMsgId + 1;

    patchConversation(convoId, (c) => ({
      ...c,
      title: c.title || text.slice(0, 50),
      messages: [
        ...c.messages,
        ...(appendUser ? [{ id: userMsgId, role: "user" as const, text }] : []),
        { id: assistantId, role: "assistant" as const, text: "" },
      ],
      updatedAt: Date.now(),
    }));

    setLoading(true);
    setActiveTools([]);
    const ac = new AbortController();
    abortRef.current = ac;

    // The id can change mid-stream (server assigns the real one)
    let currentId = convoId;

    const dropEmptyPlaceholder = () =>
      patchConversation(currentId, (c) => ({
        ...c,
        messages: c.messages.filter((m) => !(m.id === assistantId && !m.text)),
      }));

    try {
      await streamChatMessage(
        {
          message: text,
          conversationId: isLocalId(convoId) ? undefined : convoId,
          language: settingsStore.language,
          signal: ac.signal,
        },
        {
          onMeta: ({ conversationId }) => {
            if (conversationId && conversationId !== currentId) {
              const oldId = currentId;
              currentId = conversationId;
              setConversations((prev) =>
                prev.map((c) => (c.id === oldId ? { ...c, id: conversationId } : c))
              );
              setActiveId(conversationId);
            }
          },
          onTool: ({ label }) =>
            setActiveTools((prev) => (prev.includes(label) ? prev : [...prev, label])),
          onText: (delta) => {
            setActiveTools((prev) => (prev.length ? [] : prev));
            patchConversation(currentId, (c) => ({
              ...c,
              messages: c.messages.map((m) =>
                m.id === assistantId ? { ...m, text: m.text + delta } : m
              ),
            }));
          },
          onDone: ({ title, reply, actions }) => {
            patchConversation(currentId, (c) => ({
              ...c,
              title: title || c.title,
              messages: c.messages.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      text: reply || m.text,
                      ...(actions?.length ? { actions } : {}),
                    }
                  : m
              ),
              updatedAt: Date.now(),
            }));
          },
          onError: (msg) => {
            dropEmptyPlaceholder();
            setError(msg);
            setLastFailed(text);
          },
        }
      );
    } catch (err: any) {
      dropEmptyPlaceholder();
      if (err?.name !== "AbortError") {
        setError(err?.message || lang.agent.errorMessage);
        setLastFailed(text);
      }
    } finally {
      setLoading(false);
      setActiveTools([]);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    track(EVENTS.AGENT_MESSAGE_SENT, { source: "input" });
    runTurn(text, true);
  };

  const handleSuggestion = (text: string) => {
    if (loading) return;
    track(EVENTS.AGENT_MESSAGE_SENT, { source: "suggestion" });
    runTurn(text, true);
  };

  const handleRetry = () => {
    if (!lastFailed || loading) return;
    // The user message is still displayed; the server never persisted the
    // failed turn, so resend the same text without appending it again.
    runTurn(lastFailed, false);
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const handleFeedback = (msg: DisplayMessage, rating: "up" | "down") => {
    if (!activeId || isLocalId(activeId)) return;
    const next = msg.feedback === rating ? null : rating;
    patchConversation(activeId, (c) => ({
      ...c,
      messages: c.messages.map((m) =>
        m.id === msg.id ? { ...m, feedback: next } : m
      ),
    }));
    sendFeedback(activeId, msg.id, next);
  };

  const handleAction = async (action: AgentAction, approve: boolean) => {
    const convoId = activeId;
    if (!convoId || isLocalId(convoId) || resolvingActionId) return;
    setResolvingActionId(action.id);
    setError(null);
    try {
      const { messages: next } = await resolveAgentAction(convoId, action.id, approve);
      patchConversation(convoId, (c) => ({ ...c, messages: next as DisplayMessage[] }));
    } catch (err: any) {
      if (err?.status === 409 || err?.status === 404) {
        // Already handled (another tab) or the conversation moved on: show
        // the server's state instead.
        const full = await apiGetConversation(convoId);
        if (full) patchConversation(convoId, (c) => ({ ...c, messages: full.messages as DisplayMessage[] }));
      } else {
        setError(err?.message || lang.agent.errorMessage);
      }
    } finally {
      setResolvingActionId(null);
    }
  };

  const renderAction = (action: AgentAction) => {
    const t = lang.agent;
    const titles = t.actionTitles as Record<string, string>;
    const labels = t.actionFields as Record<string, string>;
    const isCreate = action.tool.startsWith("create_");
    const busy = resolvingActionId === action.id;
    return (
      <ActionCard key={action.id}>
        <ActionTitle>
          {titles[action.tool] ?? action.tool}: {action.preview.target}
        </ActionTitle>
        <ActionChanges as="dl">
          {action.preview.changes.map((change) => (
            <React.Fragment key={change.key}>
              <dt>{labels[change.key] ?? change.key}</dt>
              <dd>
                {!isCreate && <s>{formatActionValue(change.key, change.from, t)}</s>}
                {formatActionValue(change.key, change.to, t)}
              </dd>
            </React.Fragment>
          ))}
        </ActionChanges>
        {action.status === "pending" ? (
          <ActionButtons>
            <ActionConfirmBtn
              disabled={!!resolvingActionId || loading}
              onClick={() => handleAction(action, true)}
            >
              {t.actionConfirm}
            </ActionConfirmBtn>
            <ActionCancelBtn
              disabled={!!resolvingActionId || loading}
              onClick={() => handleAction(action, false)}
            >
              {t.actionCancel}
            </ActionCancelBtn>
            {busy && (
              <ThinkingDots>
                <span>{t.thinking}</span>
              </ThinkingDots>
            )}
          </ActionButtons>
        ) : (
          <ActionStatus failed={action.status === "failed"}>
            {action.status === "done"
              ? `✓ ${t.actionDone}`
              : action.status === "cancelled"
                ? t.actionCancelled
                : `${t.actionFailed}${action.error ? `: ${action.error}` : ""}`}
          </ActionStatus>
        )}
      </ActionCard>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const suggestions = [
    lang.agent.suggestedHarvest,
    lang.agent.suggestedTrials,
    lang.agent.suggestedDevices,
  ].filter(Boolean);

  const lastMessage = messages[messages.length - 1];
  const streamingEmptyReply =
    loading && lastMessage?.role === "assistant" && !lastMessage.text;

  return (
    <Layout title={lang.agent.screenTitle}>
      <ScreenRoot>
        {/* ── History sidebar (desktop only) ── */}
        {!isMobile && (
          <Sidebar>
            <SidebarHeader>
              <SidebarTitle>History</SidebarTitle>
              <NewChatBtn onClick={handleNewChat} title="New chat">+</NewChatBtn>
            </SidebarHeader>
            {conversations.length === 0 ? (
              <EmptySidebar>No conversations yet</EmptySidebar>
            ) : (
              <ConversationList>
                {conversations.map((convo) => (
                  <ConversationItem
                    key={convo.id}
                    active={convo.id === activeId}
                    onClick={() => handleSelectChat(convo.id)}
                  >
                    <ConversationTitle active={convo.id === activeId}>
                      {convo.title || "New chat"}
                    </ConversationTitle>
                    <DeleteBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteChat(convo.id);
                      }}
                      title="Delete"
                    >
                      ×
                    </DeleteBtn>
                  </ConversationItem>
                ))}
              </ConversationList>
            )}
          </Sidebar>
        )}

        {/* ── Chat area ── */}
        <ChatArea>
          {messages.length === 0 && !loading ? (
            <WelcomeContainer>
              <WelcomeText>{lang.agent.welcomeMessage}</WelcomeText>
              {suggestions.length > 0 && (
                <SuggestionRow>
                  {suggestions.map((s) => (
                    <SuggestionChip key={s} onClick={() => handleSuggestion(s)}>
                      {s}
                    </SuggestionChip>
                  ))}
                </SuggestionRow>
              )}
            </WelcomeContainer>
          ) : (
            <MessagesScroll>
              <MessagesArea>
                {messages.map((msg) => {
                  const isStreamingPlaceholder =
                    msg.id === lastMessage?.id && streamingEmptyReply;
                  if (isStreamingPlaceholder) {
                    return (
                      <MessageRow key={msg.id} isUser={false}>
                        {activeTools.length > 0 ? (
                          <ToolChipRow>
                            {activeTools.map((label) => (
                              <ToolChip key={label}>{label}</ToolChip>
                            ))}
                          </ToolChipRow>
                        ) : (
                          <ThinkingDots>
                            <span>{lang.agent.thinking}</span>
                          </ThinkingDots>
                        )}
                      </MessageRow>
                    );
                  }
                  return (
                    <MessageRow key={msg.id} isUser={msg.role === "user"}>
                      {(msg.text || !msg.actions?.length) && (
                        <MessageBubble isUser={msg.role === "user"}>
                          {msg.role === "user" ? (
                            msg.text
                          ) : (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              disallowedElements={AGENT_DISALLOWED_ELEMENTS}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          )}
                        </MessageBubble>
                      )}
                      {msg.role === "assistant" && msg.actions?.map(renderAction)}
                      {msg.role === "assistant" &&
                        msg.text &&
                        !(loading && msg.id === lastMessage?.id) &&
                        !isLocalId(activeId ?? "") && (
                          <FeedbackRow>
                            <FeedbackBtn
                              active={msg.feedback === "up"}
                              onClick={() => handleFeedback(msg, "up")}
                              title="Helpful"
                            >
                              👍
                            </FeedbackBtn>
                            <FeedbackBtn
                              active={msg.feedback === "down"}
                              onClick={() => handleFeedback(msg, "down")}
                              title="Not helpful"
                            >
                              👎
                            </FeedbackBtn>
                          </FeedbackRow>
                        )}
                    </MessageRow>
                  );
                })}
                {error && (
                  <MessageRow isUser={false}>
                    <MessageBubble isUser={false} style={{ color: "#d32f2f", background: "#fce4ec" }}>
                      {error}
                    </MessageBubble>
                    {lastFailed && (
                      <RetryBtn onClick={handleRetry}>
                        {lang.agent.retry ?? "Retry"}
                      </RetryBtn>
                    )}
                  </MessageRow>
                )}
                <div ref={messagesEndRef} />
              </MessagesArea>
            </MessagesScroll>
          )}
          <InputBarWrap>
            <InputBar onSubmit={handleSubmit}>
              <ChatInput
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={lang.agent.inputPlaceholder}
                disabled={loading}
                rows={1}
              />
              {loading ? (
                <StopButton
                  type="button"
                  onClick={handleStop}
                  title={lang.agent.stop ?? "Stop"}
                >
                  <StopIcon />
                </StopButton>
              ) : (
                <SendButton type="submit" disabled={!input.trim()}>
                  <SendIcon />
                </SendButton>
              )}
            </InputBar>
          </InputBarWrap>
        </ChatArea>
      </ScreenRoot>
    </Layout>
  );
};

export default AgentScreen;
